import React from 'react';
import { View, ViewStyle, StyleProp, TextStyle } from 'react-native';
import MyAppText from './MyAppText';
import { TextInputMask } from 'react-native-masked-text';
import { useStyles } from 'react-native-unistyles';
import { translate } from '../services/translate';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';
import inputStylesheet from '../styles/inputStyles';

type MyCpfInputProps = {
    viewStyle?: StyleProp<ViewStyle>;
    required?: boolean;
    labelStyle?: StyleProp<TextStyle>;
    disabled?: boolean;
} & ({
    value: string;
    onChangeText: (value: string) => void;
    isInvalid: boolean;
    setIsInvalid: (value: boolean) => void;
    form?: never;
    fieldName?: never;
    customValidations?: never;
    pattern?: never;
} | {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    value?: never;
    onChangeText?: never;
    isInvalid?: never;
    setIsInvalid?: never;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any) => string | true;
    };
    pattern?: {
        value: RegExp;
        message: string;
    };
});

export default function MyCpfInput({ value, onChangeText, viewStyle, isInvalid, setIsInvalid, required, form, fieldName, customValidations, pattern, disabled }: MyCpfInputProps) {
    const { styles: defaultInputStyle } = useStyles(inputStylesheet);

    function invalidCpf(cpf: string) {
        if (!cpf && !required) {
            return false;
        }
        if (cpf === '' || cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
            return true;
        }

        let sum = 0;
        for (let i = 0; i < 9; i++) {
            sum += parseInt(cpf.charAt(i)) * (10 - i);
        }
        let checkDigit1 = 11 - (sum % 11);
        if (checkDigit1 >= 10) {
            checkDigit1 = 0;
        }
        if (checkDigit1 !== parseInt(cpf.charAt(9))) {
            return true;
        }

        sum = 0;
        for (let i = 0; i < 10; i++) {
            sum += parseInt(cpf.charAt(i)) * (11 - i);
        }
        let checkDigit2 = 11 - (sum % 11);
        if (checkDigit2 >= 10) {
            checkDigit2 = 0;
        }
        if (checkDigit2 !== parseInt(cpf.charAt(10))) {
            return true;
        }

        return false;
    }

    if (form && fieldName) {
        const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

        if (required) {
            rules.required = translate('required_field');
        }

        if (pattern) {
            rules.pattern = pattern;
        }

        rules.validate = {
            validateCpf: (value) => !invalidCpf(value) || translate('invalidDocument')
        };

        if (customValidations) {
            rules.validate = {
                ...rules.validate,
                ...customValidations
            };
        }

        return <Controller
            control={form.control}
            name={fieldName}
            rules={rules}
            render={({ field, fieldState }) => (
                <View style={[defaultInputStyle.container, viewStyle]}>
                    <View style={defaultInputStyle.labelContainer}>
                        <MyAppText style={defaultInputStyle.label}>CPF</MyAppText>
                        {required ?
                            <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                            : null}
                    </View>
                    <TextInputMask
                        onBlur={field.onBlur}
                        value={field.value}
                        includeRawValueInChangeText={true}
                        onChangeText={(maskedText, rawText) => {
                            field.onChange(rawText || '');
                        }}
                        type={'cpf'}
                        style={defaultInputStyle.input({
                            invalid: Boolean(fieldState.error),
                            disabled,
                            placeholder: !field.value,
                        })}
                        placeholder='000.000.000-00'
                        editable={!disabled}
                    />
                    {isInvalid && value !== ''
                        ? <MyAppText style={defaultInputStyle.errorMessage}>{translate('invalidDocument')}</MyAppText>
                        : fieldState.error ? <MyAppText style={defaultInputStyle.errorMessage}>{fieldState.error.message}</MyAppText> : null}
                </View>
            )}
        />;

    } else if (value !== undefined && onChangeText !== undefined) {
        return (
            <View style={[defaultInputStyle.container, viewStyle]}>
                <View style={defaultInputStyle.labelContainer}>
                    <MyAppText style={defaultInputStyle.label}>CPF</MyAppText>
                    {required ?
                        <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                        : null}
                </View>
                <TextInputMask
                    value={value}
                    includeRawValueInChangeText={true}
                    onChangeText={(maskedText, rawText) => {
                        onChangeText(rawText || '');
                        if (rawText) {
                            setIsInvalid(invalidCpf(rawText));
                        }
                    }}
                    type={'cpf'}
                    style={defaultInputStyle.input({
                        invalid: Boolean(isInvalid),
                        disabled,
                        placeholder: !value,
                    })}
                    placeholder='000.000.000-00'
                    editable={!disabled}
                />
                {isInvalid && value !== '' ?
                    <MyAppText style={defaultInputStyle.errorMessage}>{translate('invalidDocument')}</MyAppText> : null}

            </View>
        );
    }
}
